import { useEffect, useRef, useState, type ReactNode } from 'react'

import {
  AnimatePresence,
  motion,
  useIsPresent,
  type Variants
} from 'motion/react'

const CONTENT_VARIANTS: Variants = {
  enter: (direction: number) => ({ opacity: 0, x: direction * 16 }),
  visible: { opacity: 1, x: 0 },
  exit: (direction: number) => ({ opacity: 0, x: direction * -16 })
}
const CONTENT_TRANSITION = {
  duration: 0.16,
  ease: [0.32, 0.72, 0, 1] as const
}
// The skeleton of a view that opens loading is there at once and leaves at
// once, so the rows that replace it make the view's slide.
const INSTANT_VARIANTS: Variants = {
  enter: { opacity: 1, x: 0 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 1, x: 0 }
}
const INSTANT_TRANSITION = { duration: 0 }

interface TableTransitionProps {
  viewKey: string
  tabIndex: number
  /** Slides the first content in too: 1 from the right, -1 from the left. */
  enterDirection?: number
  page: number
  /** Skeleton rows are showing in place of the rows. */
  loading: boolean
  animated: boolean
  minWidth: number
  onContentChange: () => void
  children: ReactNode
}

function TransitionContent({
  direction,
  instant,
  onContentChange,
  children
}: Pick<TableTransitionProps, 'onContentChange' | 'children'> & {
  direction: number
  instant: boolean
}) {
  const isPresent = useIsPresent()
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    contentRef.current?.toggleAttribute('inert', !isPresent)
  }, [isPresent])

  // Presence replaces its children after the scroll area's render. Measure the
  // new grid here so its external scrollbars reflect the arriving content.
  useEffect(onContentChange, [children, onContentChange])

  return (
    <motion.div
      ref={contentRef}
      data-slot='omni-table-transition-content'
      data-phase={instant ? 'skeleton' : 'rows'}
      aria-hidden={!isPresent || undefined}
      className='col-start-1 row-start-1 min-w-0'
      custom={direction}
      variants={instant ? INSTANT_VARIANTS : CONTENT_VARIANTS}
      initial='enter'
      animate='visible'
      exit='exit'
      transition={instant ? INSTANT_TRANSITION : CONTENT_TRANSITION}
      onAnimationComplete={onContentChange}
    >
      {/* Column layout nodes unmount while editing. Their presence bookkeeping
          must not hold the whole grid's exit open. */}
      <AnimatePresence initial={false} propagate={false}>
        {children}
      </AnimatePresence>
    </motion.div>
  )
}

export function TableTransition({
  viewKey,
  tabIndex,
  enterDirection = 0,
  page,
  loading,
  animated,
  minWidth,
  onContentChange,
  children
}: TableTransitionProps) {
  const [previous, setPrevious] = useState({
    viewKey,
    tabIndex,
    page,
    direction: enterDirection || 1,
    // A view that opens loading owes its slide to the rows that replace the
    // skeleton; a load with no view change swaps them in place.
    slideOwed: loading && enterDirection !== 0
  })
  let { direction, slideOwed } = previous
  if (previous.viewKey !== viewKey) {
    direction =
      Math.sign(
        tabIndex !== previous.tabIndex
          ? tabIndex - previous.tabIndex
          : page - previous.page
      ) || 1
    slideOwed = loading
    setPrevious({ viewKey, tabIndex, page, direction, slideOwed })
  } else if (slideOwed && !loading) {
    slideOwed = false
    setPrevious({ ...previous, slideOwed })
  }
  const skeletonOwesSlide = loading && slideOwed

  if (!animated) return children

  return (
    <div
      data-slot='omni-table-transition'
      className='grid min-w-full overflow-clip'
      style={{ minWidth }}
    >
      <AnimatePresence
        initial={enterDirection !== 0}
        mode='wait'
        custom={direction}
      >
        <TransitionContent
          key={skeletonOwesSlide ? `${viewKey}:skeleton` : viewKey}
          direction={direction}
          instant={skeletonOwesSlide}
          onContentChange={onContentChange}
        >
          {children}
        </TransitionContent>
      </AnimatePresence>
    </div>
  )
}
